import { useState, MouseEvent, useEffect, useMemo } from "react";
import {
  Avatar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  TextField,
  InputAdornment,
  Popover,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import { useAuth, type Tenant } from "../context/AuthContext";
import { useLocation, Link as RouterLink, useNavigate } from "react-router-dom";
import LogoIcon from "./icons/logoIconDarkBg";
import { Search } from "lucide-react";
import { Sparkles } from "lucide-react";
import { Icon } from "@mui/material";
import { buildCompanySwitchLoginUrl } from "../utils/companyUrl";
import { normalizeRoleForGating } from "../utils/roles";
import { ChevronRight, KeyboardArrowUp, KeyboardArrowDown } from "@mui/icons-material";

type MiniSidebarProps = {
  activePanel: "menu" | "chat" | null;
  isOpen: boolean;
  onMenuClick: () => void;
  onChatClick: () => void;
  zIndex?: number;
};

const MiniSidebar: React.FC<MiniSidebarProps> = ({
  activePanel,
  isOpen,
  onMenuClick,
  onChatClick,
  zIndex = 1,
}) => {
  const { user, logout, tenantList } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [companyMenuAnchor, setCompanyMenuAnchor] = useState<null | HTMLElement>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(
    tenantList && tenantList.length > 0 ? tenantList[0]?.id ?? null : null
  );
  const [dropdownAnchor, setDropdownAnchor] = useState<Record<string, HTMLElement | null>>({});

  const userDisplayName =
    user?.userdisplayname || user?.name || user?.email || user?.user || "User";
  const userInitial = user?.firstName 
    ? user.firstName.charAt(0).toUpperCase()
    : userDisplayName?.trim()?.[0]?.toUpperCase() || "U";

  // Get full name for display
  const getFullName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return userDisplayName;
  };

  // Update selected tenant when tenantList changes
  useEffect(() => {
    if (tenantList && tenantList.length > 0 && !selectedTenantId) {
      setSelectedTenantId(tenantList[0]?.id ?? null);
    }
  }, [tenantList, selectedTenantId]);

  const selectedTenant = tenantList?.find(
    (tenant) => tenant.id === selectedTenantId
  );

  const handleCompanyMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setCompanyMenuAnchor(event.currentTarget);
  };

  const handleCompanyMenuClose = () => {
    setCompanyMenuAnchor(null);
    setSearchTerm("");
  };

  const handleTenantSelect = (tenant: Tenant) => {
    setSelectedTenantId(tenant.id);
    handleCompanyMenuClose();

    const authToken = localStorage.getItem("accessToken");
    const id = tenant.id || "";
    const email = (tenant as any).email || "";
    const phone = (tenant as any).phone || "";
    const companyName = tenant.name || tenant.displayname || "";

    if (authToken) {
      const tenantSubdomain = (tenant as { subdomain?: string }).subdomain;
      const finalUrl = buildCompanySwitchLoginUrl(tenantSubdomain, {
        id,
        authToken,
        switch: "true",
        switchCompanyId: id,
        ...(companyName ? { companyName } : {}),
        ...(email ? { email } : {}),
        ...(phone ? { phone } : {}),
      });
      window.open(finalUrl, "_blank");
    }
  };

  const getInitials = (name?: string | null) => {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    return parts.map((part) => part.charAt(0).toUpperCase()).join("").slice(0, 1);
  };

  // Filter tenants based on search term
  const filteredTenants = useMemo(() => {
    if (!searchTerm) return tenantList || [];
    const term = searchTerm.toLowerCase();
    return (tenantList || []).filter((tenant: any) => {
      const name = (tenant.name || tenant.displayname || "").toLowerCase();
      const email = (tenant.email || "").toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }, [tenantList, searchTerm]);

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  // Define all navigation items with their allowed roles
  const allNavItems: any = [
    {
      path: "/dashboard",
      text: "Dashboard",
      Icon: "dashboard",
      allowedRoles: ["SUPER_ADMIN", "partner_admin", "PARTNER_ADMIN", "super_admin"]
    },
    {
      path: "/ticketing",
      text: "Ticketing",
      Icon: "view_kanban",
      allowedRoles: ["SUPER_ADMIN", "partner_admin", "PARTNER_ADMIN", "super_admin"]
    },
    {
      path: "/plans",
      text: "Subscription Plans",
      Icon: "card_membership",
      allowedRoles: ["SUPER_ADMIN", "super_admin"]
    },
    {
      path: "/ai-addons",
      text: "AI Addons",
      Icon: "smart_toy",
      allowedRoles: ["SUPER_ADMIN", "super_admin"]
    },
    {
      path: "/team",
      text: "Team",
      Icon: "groups",
      allowedRoles: ["SUPER_ADMIN", "super_admin", "partner_admin", "PARTNER_ADMIN"]
    },
    {
      path: "/partners",
      text: "Partners",
      Icon: "people",
      allowedRoles: ["SUPER_ADMIN", "super_admin"]
    },
    {
      path: "/company",
      text: "Company",
      Icon: "business",
      allowedRoles: ["SUPER_ADMIN", "super_admin", "partner_admin", "PARTNER_ADMIN"]
    },
    {
      path: "/requested-products",
      text: "Requested Products",
      Icon: "pending_actions",
      allowedRoles: ["SUPER_ADMIN", "super_admin"]
    },
    {
      path: "/manufacturer",
      text: "Manufacturer",
      Icon: "factory",
      allowedRoles: ["SUPER_ADMIN", "super_admin"]
    },
    {
      path: "/productcategory",
      text: "Product Category",
      Icon: "category",
      allowedRoles: ["SUPER_ADMIN", "super_admin"]
    },
    {
      path: "/producttype",
      text: "Product Type",
      Icon: "warehouse",
      allowedRoles: ["SUPER_ADMIN", "super_admin"]
    },
    {
      path: "/products",
      text: "Products",
      Icon: "inventory_2",
      allowedRoles: ["SUPER_ADMIN", "super_admin"]
    },
    {
      path: "/servicetype",
      text: "Service Type",
      Icon: "service_toolbox",
      allowedRoles: ["SUPER_ADMIN", "super_admin"]
    },
    {
      path: "/workorderstage",
      text: "Workorder Stages",
      Icon: "timeline",
      allowedRoles: ["SUPER_ADMIN", "super_admin"]
    },
    // {
    //   path: "/document-types-prompt",
    //   text: "Document Types Prompt",
    //   Icon: "description",
    //   allowedRoles: ["SUPER_ADMIN", "super_admin"]
    // },
    {
      path: "/prompt-library",
      text: "Prompt Library",
      Icon: "terminal",
      allowedRoles: ["SUPER_ADMIN", "super_admin"]
    },
    {
      path: "/lens",
      text: "Lenses",
      Icon: "policy",
      allowedRoles: ["SUPER_ADMIN", "super_admin"]
    },
  ];

  // Filter navItems based on user role
  const navItems = useMemo(() => {
    const userRole = (user as any)?.role;
    if (!userRole) {
      return [];
    }
    const normalizedUserRoleForGating = normalizeRoleForGating(String(userRole));
    return allNavItems.filter((item: any) => {
      if (!item.allowedRoles || item.allowedRoles.length === 0) {
        return true;
      }
      return item.allowedRoles.map((r: string) => r.toLowerCase()).includes(normalizedUserRoleForGating);
    });
  }, [user]);

  const isMenuOpen = Boolean(menuAnchorEl);
  const isMenuPanelActive = activePanel === "menu" && isOpen;
  const isChatPanelActive = activePanel === "chat" && isOpen;

  return (
    <Box
      sx={{
        backgroundColor: "black",
        color: "white",
        height: "100vh",
        width: "80px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
        zIndex,
        flexShrink: 0,
        borderRight: "1px solid #444",
      }}
    >
      {/* Fixed Top Section - Logo */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          py: 2,
          flexShrink: 0,
        }}
      >
        <LogoIcon width="32px" height="28px" />
      </Box>

      {/* Scrollable Middle Section - Navigation Icons */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.75,
          width: "100%",
          flex: 1,
          overflowY: "overlay",
          overflowX: "hidden",
          "&::-webkit-scrollbar": {
            width: "3px",
            background: "transparent",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
            border: "none",
            margin: 0,
            padding: 0,
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#666",
            borderRadius: "10px",
            border: "none",
            minHeight: "20px",
            "&:hover": {
              background: "#777",
            },
          },
          "&::-webkit-scrollbar-corner": {
            background: "transparent",
          },
          // Firefox scrollbar
          scrollbarWidth: "thin",
          scrollbarColor: "#666 transparent",
        }}
      >

        {navItems.map((nav: any) => {
            const isActive = nav.path
              ? location.pathname.startsWith(nav.path)
              : false;

            if (!nav.isParent) {
              return (
                <Tooltip key={nav.text} title={nav.text} placement="right" arrow>
                  <IconButton
                    component={RouterLink}
                    to={nav.path}
                    size="medium"
                    sx={{
                      backgroundColor: isActive ? "#3b82f6" : "transparent",
                      color: isActive ? "white" : "rgba(255, 255, 255, 0.72)",
                      "&:hover": { backgroundColor: "#3b82f6", color: "white" },
                      width: "36px",
                      height: "36px",
                    }}
                  >
                    <Icon fontSize="small" className="material-symbols-outlined" sx={{ fontSize: "18px" }}>
                      {nav.Icon}
                    </Icon>
                  </IconButton>
                </Tooltip>
              );
            }

            // Parent item with children (e.g., Master Data)
            const menuKey = nav.text;
            const dropdownOpen = Boolean(dropdownAnchor[menuKey]);
            const isParentActive = nav.children?.some((child: any) =>
              location.pathname.startsWith(child.path)
            ) ?? false;

            return (
              <Box key={nav.text}>
                <Tooltip title={nav.text} placement="right" arrow>
                  <IconButton
                    onClick={(e) => {
                      setDropdownAnchor({
                        ...dropdownAnchor,
                        [menuKey]: e.currentTarget,
                      });
                    }}
                    size="medium"
                    sx={{
                      backgroundColor: isParentActive ? "#3b82f6" : "transparent",
                      color: isParentActive ? "white" : "rgba(255, 255, 255, 0.72)",
                      "&:hover": { backgroundColor: "#3b82f6", color: "white" },
                      position: "relative",
                      width: "36px",
                      height: "36px",
                    }}
                  >
                    <Icon fontSize="small" className="material-symbols-outlined" sx={{ marginRight: "4px", fontSize: "18px" }}>
                      {nav.Icon}
                    </Icon>
                    <ChevronRight 
                      sx={{ 
                        position: "absolute",
                        right: "6px",
                        fontSize: "16px",
                        color: "inherit",
                        opacity: 0.8,
                      }} 
                    />
                  </IconButton>
                </Tooltip>
                <Popover
                  open={dropdownOpen}
                  anchorEl={dropdownAnchor[menuKey]}
                  onClose={() => {
                    setDropdownAnchor({
                      ...dropdownAnchor,
                      [menuKey]: null,
                    });
                  }}
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  PaperProps={{
                    sx: {
                      bgcolor: "black",
                      color: "white",
                      minWidth: 200,
                      mt: 0.5,
                    },
                  }}
                >
                  <List dense>
                    {nav.children?.map((child: any) => {
                      const isChildActive = location.pathname.startsWith(child.path);
                      return (
                        <ListItemButton
                          key={child.path}
                          component={RouterLink}
                          to={child.path}
                          onClick={() => {
                            setDropdownAnchor({
                              ...dropdownAnchor,
                              [menuKey]: null,
                            });
                          }}
                          sx={{
                            color: isChildActive ? "#fff" : "rgba(255, 255, 255, 0.72)",
                            bgcolor: isChildActive ? "#3b82f6" : "transparent",
                            "&:hover": {
                              bgcolor: "#3b82f6",
                              color: "#fff",
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 32,
                              color: "inherit",
                            }}
                          >
                            <Icon fontSize="small" className="material-symbols-outlined">
                              {child.Icon}
                            </Icon>
                          </ListItemIcon>
                          <ListItemText
                            primary={child.text}
                            primaryTypographyProps={{
                              sx: {
                                color: "inherit",
                                fontSize: "14px",
                              },
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Popover>
              </Box>
            );
          })}
      </Box>

      {/* Fixed Bottom Section - User Icon */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          py: 2,
          flexShrink: 0,
        }}
      >
        <Tooltip title={getFullName()} placement="right" arrow>
          <IconButton
            aria-label="User menu"
            onClick={handleMenuOpen}
            id="mini-sidebar-user-menu-button"
            size="large"
            aria-haspopup="true"
            aria-controls={isMenuOpen ? "mini-sidebar-user-menu" : undefined}
            aria-expanded={isMenuOpen ? "true" : undefined}
            sx={{
              padding: 0,
              borderRadius: "9999px",
              color: "#ffffff",
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                fontSize: 14,
                backgroundColor: "#2d3748",
              }}
            >
              {userInitial}
            </Avatar>
          </IconButton>
        </Tooltip>
        <Menu
          id="mini-sidebar-user-menu"
          anchorEl={menuAnchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          TransitionProps={{ timeout: 150 }}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "bottom", horizontal: "right" }}
          PaperProps={{
            sx: {
              mt: -1,
              minWidth: 180,
              backgroundColor: "black",
              color: "white",
              borderRadius: 2,
              border: "1px solid #444",
            },
          }}
          MenuListProps={{ "aria-labelledby": "mini-sidebar-user-menu-button" }}
        >
          <MenuItem disabled sx={{ opacity: 1, fontWeight: 600 }}>
            <Typography variant="h6" sx={{ fontSize: "14px", color: '#fff', fontWeight: 700 }}>
              {getFullName()}
            </Typography>
          </MenuItem>
          <MenuItem 
            onClick={() => {
              handleMenuClose();
              navigate('/settings');
            }} 
            sx={{ fontSize: "0.875rem", color: "#fff" }}
          >
            Profile
          </MenuItem>
          <MenuItem 
            onClick={handleLogout} 
            sx={{ fontSize: "0.875rem", color: "#fff" }}
          >
            Logout
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default MiniSidebar;
